import type { FC } from "react";
import { useUserSession } from "../../../context/UserContext";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import { Button, DefaultSkeleton } from "@components";
import { Timestamp } from "../../../types";
import { useState, useEffect } from "react";
import { SettingsRowProps } from "../components/SettingsRow";
import User from "../../../models/User";

interface ProfileSettingsContentProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
}

const ProfileSettingsContent: FC<ProfileSettingsContentProps> = ({
  SettingsRow,
}) => {
  const { userSession, setUserSession } = useUserSession();
  const { t } = useTranslation(["common", "user"]);
  const [displayName, setDisplayName] = useState(
    userSession?.user.displayName || "",
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update displayName when userSession changes
  useEffect(() => {
    setDisplayName(userSession?.user.displayName || "");
  }, [userSession?.user.displayName]);

  if (userSession == null) {
    return <DefaultSkeleton />;
  }

  const handleSaveDisplayName = async () => {
    setIsSaving(true);
    try {
      if (!userSession?.user) {
        console.error("No user session available");
        return;
      }

      // Create a User instance from the current user session
      const currentUser = new User(userSession.user);

      // Update the user in Firebase
      const updatedUser = await User.update(currentUser, { displayName });

      // Update the local userSession to reflect the change immediately
      setUserSession({
        ...userSession,
        user: {
          ...userSession.user,
          displayName: updatedUser.displayName,
        },
      });

      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update display name:", error);
      // Reset display name to original value on error
      setDisplayName(userSession?.user.displayName || "");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setDisplayName(userSession?.user.displayName || "");
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-text sm:text-2xl mb-2">
          {t("user:profile.title")}
        </h2>
        <p className="text-secondary text-base dark:text-secondary">
          {t("user:profile.description")}
        </p>
      </div>

      <div className="space-y-0">
        <SettingsRow
          title={t("user:profile.display_name")}
          description={t("user:profile.display_name_description")}
        >
          {isEditing ? (
            <div className="flex items-center gap-2">
              <TextInput
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t("user:profile.display_name_placeholder")}
                className="w-48"
              />
              <Button
                size="sm"
                color="blue"
                onClick={handleSaveDisplayName}
                disabled={isSaving}
              >
                {isSaving ? t("common:saving") : t("common:save")}
              </Button>
              <Button
                size="sm"
                color="light"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                {t("common:cancel")}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-text">
                {userSession.user.displayName || t("common:not_set")}
              </span>
              <Button
                size="sm"
                color="light"
                onClick={() => setIsEditing(true)}
              >
                {t("common:edit")}
              </Button>
            </div>
          )}
        </SettingsRow>

        <SettingsRow
          title={t("common:email")}
          description={t("user:profile.email_description")}
        >
          <span className="text-text">
            {userSession.user.email}
          </span>
        </SettingsRow>

        <SettingsRow
          title={t("user:profile.account_created")}
          description={t("user:profile.account_created_description")}
          separator={false}
        >
          <span className="text-text">
            {userSession.user.createdAt
              ? Timestamp.toDisplayDate(userSession.user.createdAt)
              : t("common:no_date")}
          </span>
        </SettingsRow>
      </div>
    </div>
  );
};

export default ProfileSettingsContent;
